import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { FixMeLater } from 'types'
import {
  useLoginMutation,
  useSendResetPasswordMailMutation,
} from '../../../app/services/auth.service'

import { LOGOUT } from 'utils/constants'
import { clientChannel } from 'utils/constants'
import { deleteFCMToken, signOutFirebase, getFCMToken } from 'services/firebase.service'
import { authActions } from 'app/slices/auth.slice'
import KVKKModal from 'components/modals/KVKKModal'
import Loading from 'components/spinner/Loading'
import CustomButton from 'components/button/CustomButton'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import ForgotPasswordModal from 'components/modals/ForgotPasswordModal'
import styled from 'styled-components'
import { BUTTONS, FORM, PLACEHOLDERS, TITLES } from 'locales/locales'
import { emailValidation, requiredValidation } from 'utils/helpers/validations'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useSocket } from 'utils/hooks/useSocket'
import CustomText from 'components/text/CustomText'
import colors from 'utils/colors'
import getBaseUrl from 'utils/helpers/apiHelper'
import {
  
  useUpdateUserKvkkConsentMutation,
} from 'app/services/user.service'
import Mfa from '../mfa/Mfa'
import usePermissionBasedNavigation from 'utils/hooks/usePermissionBasedNavigation'
import { getDataFromDB, removeDataFromDB } from 'utils/helpers/storageHelper'
import { icons } from 'utils/icons'
import { useGetConfigMutation } from 'app/services/system.service'
import { useLogoutMutation } from 'app/services/auth.service'
import { useImageUrl } from 'utils/hooks/useLogoUrl'

// Login Flow Statuses
type LoginFlowStep = 'idle' | 'mfa' | 'kvkk' | 'ready_to_navigate'

export default function Login() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formChecked, setFormChecked] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [resetPassFormChecked, setResetPassFormChecked] = useState(false)
  const [captchaInput, setCaptchaInput] = useState('')
  const [captchaSrc, setCaptchaSrc] = useState('')
  
  const [loginFlowStep, setLoginFlowStep] = useState<LoginFlowStep>('idle')
  const [secretToken, setSecretToken] = useState(false)
  const [clientId, setClientId] = useState<string>('')
  const [privacyText, setPrivacyText] = useState('')
  const [user, setUser] = useState<FixMeLater | null>(null)


  const clientStateId = useAppSelector((state) => state.hash.clientId)
  const isMfaEnabled = useAppSelector((state) => state.system.data.features?.mfaEnabled)
  const currentUserId = useAppSelector((state) => state.auth.id)
  const systemSettings = useAppSelector((state) => state.system.data)
  const { imageUrl: logoUrl } = useImageUrl(systemSettings?.imageUrlLogo)

  const dispatch = useAppDispatch()
  const { navigateBasedOnPermissions, isReady } = usePermissionBasedNavigation()

  const [updateKvkkConsent] = useUpdateUserKvkkConsentMutation()
  const [logout] = useLogoutMutation()
  const { state } = useLocation()
  const socket = useSocket()

  const [login] = useLoginMutation()
  // const [updateFcm] = useUpdateUserFcmMutation()
  const [sendResetPasswordMail] = useSendResetPasswordMailMutation()
  const [fetchConfig] = useGetConfigMutation()

  const closePrivacyModal = () => {
    alert('Please accept or reject')
  }

  const shouldShowKvkkModal = useCallback(
    (userKvkkStatus: boolean): boolean => {
      if (!systemSettings?.features?.isPrivacyTextEnabled) return false
      return !userKvkkStatus
    },
    [systemSettings?.features?.isPrivacyTextEnabled],
  )

  useEffect(() => {
    if (loginFlowStep === 'ready_to_navigate' && isReady) {
      navigateBasedOnPermissions()
    }
  }, [loginFlowStep, isReady, navigateBasedOnPermissions])


  useEffect(() => {
    if (!user || !currentUserId) return
    if (loginFlowStep !== 'idle') return
    
    // Eğer kullanıcı zaten login olmuşsa ve KVKK onayı gerekiyorsa
    if (shouldShowKvkkModal(user?.isKvkkAccepted)) {
      setLoginFlowStep('kvkk')
    } else if (isReady) {
      // KVKK onayı gerekmiyorsa ve permissions hazırsa direkt navigate et
      setLoginFlowStep('ready_to_navigate')
    }
  }, [user, currentUserId, loginFlowStep, shouldShowKvkkModal, isReady]) 

  const handleAcceptPrivacy = async () => {
    await updateKvkkConsent({ isKvkkAccepted: true })
    dispatch(authActions.updateUsersKvkkConsent({ isKvkkAccepted: true }))
    // KVKK kabul edildi, navigate'e geç
    setLoginFlowStep('ready_to_navigate')
  }

  const handleRejectPrivacy = async () => {
    await updateKvkkConsent({ isKvkkAccepted: false })
    dispatch(authActions.updateUsersKvkkConsent({ isKvkkAccepted: false }))

    //TODO: Logout custom hook'a alınacak

    clientChannel.postMessage({ type: LOGOUT })
    await logout({})

    const firebaseToken = await getFCMToken()

    try {
      if (firebaseToken) {
        await deleteFCMToken()
      }
      await signOutFirebase()
      await removeDataFromDB('vimesoft', 'fcmToken')
    } catch (error) {
      console.error('Oturum kapatma hatası:', error)
    }

    await removeDataFromDB('vimesoft', 'persist:root')

    dispatch({ type: 'RESET' })
    if (socket && socket.connected) {
      socket.disconnect()
    }
    // Flow'u idle'a geri al
    setLoginFlowStep('idle')
    window.location.reload()
  }

  const refreshCaptcha = () => {
    const uniqueString = Date.now().toString()
    if (clientId) {
      const newSrc = `${getBaseUrl()}api/config/generate/captcha.png?clientId=${clientId}&_=${uniqueString}`
      setCaptchaSrc(newSrc)
      setCaptchaInput('')
    }
  }

  useEffect(() => {
    const initializeSystem = async () => {
      await fetchConfig({})
      await systemInitializationTest()
      const config = await getDataFromDB('vimesoft', 'config') as { privacyText: string }
        
      if (config?.privacyText) {
          setPrivacyText(config?.privacyText)
          
        }
    }

    initializeSystem()
  }, [])

  useEffect(() => {
    if (clientId || clientStateId) {
      setClientId(clientStateId)
      refreshCaptcha()
    }
  }, [clientId, clientStateId])

  const systemInitializationTest = async () => {
    sessionStorage.setItem('isPageRefresh', 'true')
  }

  const formSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFormChecked(true)

    const { isValid: isValidEmail } = emailValidation(
      state?.params?.email ? state?.params?.email : email,
    )
    const { isValid: isValidPass } = requiredValidation(password)
    try {
      Loading.ref.show()
      // Log user in if data is valid.
      if (isValidEmail && isValidPass) {
        const existingClientId: any = await getDataFromDB('vimesoft', 'clientId')

        const loginParams: { email: string; password: string; clientId: string; captcha?: string } =
          {
            email: state?.params?.email ? state?.params?.email.trim() : email?.trim(),
            password: password.trim(),
            clientId: existingClientId,
          }

        if (systemSettings?.features?.captchaEnabled) {
          loginParams.captcha = captchaInput.trim()
        }
        const loginResult: FixMeLater = await login(loginParams)
        if (loginResult.data.success) {
          setUser(loginResult.data?.user)
          const showKvkkStep = shouldShowKvkkModal(loginResult.data?.user?.isKvkkAccepted)
          if (isMfaEnabled) {
            setSecretToken(loginResult.data.secret)
            setLoginFlowStep('mfa')
          } else if (showKvkkStep) {
            setLoginFlowStep('kvkk')
          } else {
            // MFA ve KVKK gerekmiyorsa direkt navigate'e geç
            setLoginFlowStep('ready_to_navigate')
          }
        } 
      }
    } catch (error) {
      refreshCaptcha()
    } finally {
      Loading.ref.hide()
    }
  }

  const closeSettingsModal = () => setIsModalVisible(false)

  const handleForgotPassword = async (e: FixMeLater) => {
    e.preventDefault()
    setResetPassFormChecked(true)
    const { isValid: isValidEmail } = emailValidation(email)
    if (isValidEmail) {
      Loading.ref.show()
      await sendResetPasswordMail({ email })
      Loading.ref.hide()
      setIsModalVisible(false)
      setResetPassFormChecked(false)
    }
  }

  const isValidPass = formChecked && requiredValidation(password, t(FORM.PASSWORD)).errorText
  const isValidEmail =
    formChecked && emailValidation(state?.params?.email ? state?.params?.email : email).errorText
  
  return (
    <Wrapper>
      {loginFlowStep === 'kvkk' && (
        <KVKKModal
          privacyText={privacyText}
          onAccept={handleAcceptPrivacy}
          onReject={handleRejectPrivacy}
          onClose={closePrivacyModal}
        />
      )}
      {loginFlowStep === 'mfa' && (
        <Mfa
          secret={secretToken}
          clientId={clientId}
          email={email}
          password={password}
          captcha={captchaInput}
          onSuccess={(result: any) => {
            if (result.data.success) {
              const needsKvkk = shouldShowKvkkModal(result.data.user?.isKvkkAccepted)
              if (needsKvkk) {
                setLoginFlowStep('kvkk')
              } else {
                setLoginFlowStep('ready_to_navigate')
              }
            }
          }}
        />
      )}

      {loginFlowStep === 'idle' && (
        <>
          <LogoWrapper>
            <Logo src={logoUrl} alt="logo" />
          </LogoWrapper>
          <Form onSubmit={formSubmitHandler}>
            <CustomFormInput
              value={state?.params?.email ?? email}
              type="email"
              title={t(FORM.EMAIL) as string}
              placeholder={t(PLACEHOLDERS.EMAIL) as string}
              preset="medium"
              errorText={isValidEmail}
              onChange={setEmail}
              disabled={!!state?.params?.email}
            />
            <CustomFormInput
              value={password}
              type="password"
              title={t(FORM.PASSWORD) as string}
              placeholder={t(PLACEHOLDERS.PASSWORD) as string}
              preset="medium"
              errorText={isValidPass}
              onChange={setPassword}
            />
            {systemSettings?.features?.captchaEnabled && clientId && (
              <>
                <CaptchaImg src={captchaSrc} alt="CAPTCHA" />
                <RefreshButton
                  text={t(FORM.RELOAD) as string}
                  onClick={
                    ((e: any) => {
                      e.preventDefault()
                      refreshCaptcha()
                    }) as any
                  }
                  buttonType="button"
                  icon={icons.refreshIcon}
                  size="large"
                  iconAlignment="left"
                  type="primary"
                />
                <CustomFormInput
                  title={t(FORM.CAPTCHA) as string}
                  type="text"
                  preset="medium"
                  value={captchaInput}
                  onChange={setCaptchaInput}
                />
              </>
            )}

            <CustomButton typography="header1" size="large" text={t(BUTTONS.SIGN_IN) as string} />
            {systemSettings?.features?.passwordResetEnabled && (
              <CustomText
                onClick={() => setIsModalVisible(true)}
                typography="body9"
                textAlign="center"
                cursor="pointer"
                color={colors.vime}
              >
                {t(TITLES.FORGOT_PASSWORD)}
              </CustomText>
            )}
          </Form>
          <ForgotPasswordModal
            onSubmit={handleForgotPassword}
            closeModal={closeSettingsModal}
            visible={isModalVisible}
            value={email}
            errorText={resetPassFormChecked ? emailValidation(email).errorText : ''}
            onChangeValue={setEmail}
          />
        </>
      )}
    </Wrapper>
  )
}

const LogoWrapper = styled.div`
  max-height: 200px;
  max-width: 300px;
`

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  gap: 70px;
`

const Form = styled.form`
  width: 372px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

// const Row = styled.div`
//   display: -ms-flexbox;
//   display: flex;
//   -ms-flex-wrap: wrap;
//   flex-wrap: wrap;
//   position: relative;
// `

const CaptchaImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  margin-bottom: 15px;
`

const RefreshButton = styled(CustomButton)`
  cursor: pointer;
  padding: 12px 10px;
`
